import axios from 'axios';
import {
  getRefreshToken,
  removeRefreshToken,
  logout,
} from 'actions/user';
import { Cookies } from 'react-cookie';
import {
  AUTH_TOKEN_EXPIRED,
  BAD_CREDENTIALS,
  FORBIDDEN_ACCESS,
  ACCOUNT_DISABLED,
} from '../constants';
import { isSSR } from './view';

const AUTH_TOKEN_KEY = 'auth-token';
const cookieDomain = `.${process.env.SITE_DOMAIN}`;
const authCookieOptions = {
  path: '/',
  domain: cookieDomain,
};

class ApiClient {
  constructor(dispatch, cookies = null, additionalHeaders = {}) {
    this._dispatch = dispatch;
    this._cookies = cookies ?? new Cookies();
    this.additionalHeaders = additionalHeaders;
    this.instance = axios.create({
      baseURL: process.env.API_URL,
      timeout: 5000,
    });
    this._refreshTokenPromise = null;

    this._setupInterceptors();
  }

  _getHeaders(headers = {}) {
    const { authToken } = this;

    return {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      ...(authToken ? {
        'X-Auth-Token': authToken,
      } : {}),
      ...headers,
      ...this.additionalHeaders,
    };
  }

  _setupInterceptors() {
    this.instance.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (!error.response) {
          return Promise.reject(error);
        }

        const {
          data: body, config, statusText, status,
        } = error.response;
        const { url } = config;
        const { errorCode } = body;

        if (errorCode === BAD_CREDENTIALS) {
          this.removeAuthToken();

          if (this._dispatch) {
            this._dispatch(logout());
          }

          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({
            url, status, statusText, body,
          });
        }

        if ([FORBIDDEN_ACCESS, AUTH_TOKEN_EXPIRED, ACCOUNT_DISABLED].includes(errorCode)) {
          const retry = (authToken) => {
            config._retried = true;
            config.headers['X-Auth-Token'] = authToken;
            return this.instance.request(config);
          };

          const canRefresh = !isSSR()
            && errorCode !== ACCOUNT_DISABLED
            && url !== '/auth/token' // the refresh call must never re-enter this branch
            && !config._retried; // one retry per request, never a refresh loop

          // the token was already refreshed while this request was in flight, just replay it
          if (canRefresh && this.authToken && this.authToken !== config.headers['X-Auth-Token']) {
            return retry(this.authToken);
          }

          const refreshToken = canRefresh && getRefreshToken();

          if (refreshToken) {
            // If a refresh token request is already in progress, wait for it
            if (!this._refreshTokenPromise) {
              this._refreshTokenPromise = this.instance.post('/auth/token', { refreshToken })
                .then((res) => {
                  this.setAuthToken(res.authToken);
                  return res.authToken;
                })
                .catch((err) => {
                  // only drop the refresh token when the server actually rejected it,
                  // a network error / timeout has no `body`
                  if (err && err.body) {
                    removeRefreshToken();
                  }

                  throw err;
                })
                .finally(() => {
                  this._refreshTokenPromise = null;
                });
            }

            return this._refreshTokenPromise
              .then((authToken) => {
                if (!authToken) {
                  return Promise.reject();
                }

                // Retry the original request with updated auth token
                return retry(authToken);
              })
              .catch(() => {
                this.removeAuthToken();

                // eslint-disable-next-line prefer-promise-reject-errors
                return Promise.reject({
                  url, status, statusText, body,
                });
              });
          }

          this.removeAuthToken();

          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({
            url, status, statusText, body,
          });
        }

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          url, status, statusText, body,
        });
      },
    );
  }

  get(url, headers) {
    return this.instance
      .get(url, {
        headers: this._getHeaders(headers),
      });
  }

  post(url, payload, headers) {
    return this.instance
      .post(url, payload, {
        headers: this._getHeaders(headers),
      });
  }

  put(url, payload, headers) {
    return this.instance
      .put(url, payload, {
        headers: this._getHeaders(headers),
      });
  }

  delete(url, headers) {
    return this.instance
      .delete(url, {
        headers: this._getHeaders(headers),
      });
  }

  head(url, headers) {
    return this.instance
      .head(url, {
        headers: this._getHeaders(headers),
      });
  }

  get authToken() {
    return this._cookies.get(AUTH_TOKEN_KEY);
  }

  setAuthToken(token) {
    this._cookies.set(AUTH_TOKEN_KEY, token, authCookieOptions);
  }

  removeAuthToken() {
    this._cookies.remove(AUTH_TOKEN_KEY, authCookieOptions);
  }
}

const api = new ApiClient(null);

export const initApi = (dispatch, cookies = null, additionalHeaders = {}) => {
  // Return a fresh API instance bound to this request/store to avoid cross-request leakage in SSR
  return new ApiClient(dispatch, cookies, additionalHeaders);
};

export default api;
